import { memo, useMemo, useEffect } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import AccessGroupsDropdown from './PublishNoticeAccessGroupsDropdown';
import useFetchAccessGroups from '../../hooks/useFetchAccessGroups';
import { AccessControlTypes, AccessGroupsBasedAccessControlTypes } from '../../constants/AccessControlTypes';
import { allFinishedWithSuccess } from 'settings-ui-lib/utils/requestStatusChecks';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PublishNoticeContentAccessInfo = ({
  contentType,
  selectedContentSettingsInfo,
  onAGsForAccessGroupMembershipFetchSuccess
}) => {
  var _agsByAccessControlTy;
  const {
    contentCountByAccessControlType,
    sharedAccessControlType,
    agIds
  } = selectedContentSettingsInfo;

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const agIdsToFetch = useMemo(() => {
    if (sharedAccessControlType === AccessControlTypes.ACCESS_GROUP_MEMBERSHIP) {
      return agIds === null || agIds === void 0 ? void 0 : agIds.accessGroupMembership;
    }
    if (sharedAccessControlType === AccessControlTypes.SSO_WITH_ACCESS_GROUPS) {
      return agIds === null || agIds === void 0 ? void 0 : agIds.ssoWithAccessGroups;
    }
    // When selected content possibly includes some with `ACCESS_GROUP_MEMBERSHIP` and/or `SSO_WITH_ACCESS_GROUPS`
    const totalAGIdsToFetch = [];
    if (contentCountByAccessControlType.accessGroupMembership && agIds !== null && agIds !== void 0 && agIds.accessGroupMembership) {
      totalAGIdsToFetch.push(...agIds.accessGroupMembership);
    }
    if (contentCountByAccessControlType.ssoWithAccessGroups && agIds !== null && agIds !== void 0 && agIds.ssoWithAccessGroups) {
      totalAGIdsToFetch.push(...agIds.ssoWithAccessGroups);
    }
    return totalAGIdsToFetch;
  }, [agIds === null || agIds === void 0 ? void 0 : agIds.accessGroupMembership, agIds === null || agIds === void 0 ? void 0 : agIds.ssoWithAccessGroups, contentCountByAccessControlType.accessGroupMembership, contentCountByAccessControlType.ssoWithAccessGroups, sharedAccessControlType]);
  const {
    accessGroupsRequestStatus,
    accessGroups
  } = useFetchAccessGroups({
    accessGroupIds: agIdsToFetch,
    shouldFetchData: !!agIdsToFetch && agIdsToFetch.length > 0
  });
  const agsByAccessControlType = useMemo(() => {
    let forAccessGroupMemberships = [];
    let forSSOWithAccessGroups = [];
    if (sharedAccessControlType === AccessControlTypes.ACCESS_GROUP_MEMBERSHIP) {
      forAccessGroupMemberships = accessGroups;
    } else if (sharedAccessControlType === AccessControlTypes.SSO_WITH_ACCESS_GROUPS) {
      forSSOWithAccessGroups = accessGroups;
    } else {
      accessGroups.forEach(accessGroup => {
        var _agIds$accessGroupMem, _agIds$ssoWithAccessG;
        if (agIds !== null && agIds !== void 0 && (_agIds$accessGroupMem = agIds.accessGroupMembership) !== null && _agIds$accessGroupMem !== void 0 && _agIds$accessGroupMem.includes(accessGroup.id)) {
          forAccessGroupMemberships.push(accessGroup);
        }
        if (agIds !== null && agIds !== void 0 && (_agIds$ssoWithAccessG = agIds.ssoWithAccessGroups) !== null && _agIds$ssoWithAccessG !== void 0 && _agIds$ssoWithAccessG.includes(accessGroup.id)) {
          forSSOWithAccessGroups.push(accessGroup);
        }
      });
    }
    return {
      [AccessControlTypes.ACCESS_GROUP_MEMBERSHIP]: forAccessGroupMemberships,
      [AccessControlTypes.SSO_WITH_ACCESS_GROUPS]: forSSOWithAccessGroups
    };
  }, [accessGroups, agIds === null || agIds === void 0 ? void 0 : agIds.accessGroupMembership, agIds === null || agIds === void 0 ? void 0 : agIds.ssoWithAccessGroups, sharedAccessControlType]);
  useEffect(() => {
    if (allFinishedWithSuccess(accessGroupsRequestStatus)) {
      onAGsForAccessGroupMembershipFetchSuccess(agsByAccessControlType[AccessControlTypes.ACCESS_GROUP_MEMBERSHIP]);
    }
  }, [accessGroupsRequestStatus, agsByAccessControlType, onAGsForAccessGroupMembershipFetchSuccess]);
  const selectedContentAccessControlTypes = Object.keys(contentCountByAccessControlType);
  return /*#__PURE__*/_jsxs(Alert, {
    type: "info",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "contentAccessControl.header.title"
    }),
    marginBlock: "400",
    children: [!!sharedAccessControlType && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `contentAccessControl.settingsPublishNotice.singleAccessType.${sharedAccessControlType}.${contentType}`,
        options: {
          count: contentCountByAccessControlType[sharedAccessControlType],
          noOfAccessGroups: (_agsByAccessControlTy = agsByAccessControlType[sharedAccessControlType]) === null || _agsByAccessControlTy === void 0 ? void 0 : _agsByAccessControlTy.length
        }
      }), AccessGroupsBasedAccessControlTypes.includes(sharedAccessControlType) && agsByAccessControlType[sharedAccessControlType].length > 0 && /*#__PURE__*/_jsx(AccessGroupsDropdown, {
        accessGroups: accessGroups,
        accessGroupsRequestStatus: accessGroupsRequestStatus
      })]
    }), !sharedAccessControlType && /*#__PURE__*/_jsx(List, {
      styled: true,
      paddingInlineStart: "600",
      children: selectedContentAccessControlTypes.map(accessType => {
        var _agsByAccessControlTy2;
        return /*#__PURE__*/_jsxs(ListItem, {
          children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: `contentAccessControl.settingsPublishNotice.multipleAccessTypes.${accessType}.${contentType}`,
            options: {
              count: contentCountByAccessControlType[accessType],
              noOfAccessGroups: (_agsByAccessControlTy2 = agsByAccessControlType[accessType]) === null || _agsByAccessControlTy2 === void 0 ? void 0 : _agsByAccessControlTy2.length
            }
          }), AccessGroupsBasedAccessControlTypes.includes(accessType) && agsByAccessControlType[accessType].length > 0 && /*#__PURE__*/_jsx(AccessGroupsDropdown, {
            accessGroups: agsByAccessControlType[accessType],
            accessGroupsRequestStatus: accessGroupsRequestStatus
          })]
        }, accessType);
      })
    })]
  });
};
export default /*#__PURE__*/memo(PublishNoticeContentAccessInfo);